import React from 'react';
import { AlertCircle, Clock, Zap, HelpCircle, X } from 'lucide-react';
import { theme } from '../../styles/theme';

interface Alert {
  // The backend `add_alert` contract is open-ended: besides rate_limit /
  // high_tokens / shape_unknown it also emits `error`, `session_expired`, and
  // arbitrary suspension codes (e.g. PIPELINE_WORKER_TIMEOUT). Keep this a
  // string and rely on DEFAULT_ALERT_CONFIG for any type without an entry.
  type: string;
  message: string;
  details?: string;
}

interface AlertsBannerProps {
  alerts?: Alert[];
}

interface AlertStyle {
  icon: typeof AlertCircle;
  title: string;
  bgColor: string;
  borderColor: string;
  iconColor: string;
}

const ALERT_CONFIG: Record<string, AlertStyle> = {
  rate_limit: {
    icon: Clock,
    title: 'Rate Limit Warning',
    bgColor: 'bg-qc-warning/10',
    borderColor: 'border-qc-warning/30',
    iconColor: 'text-qc-warning',
  },
  high_tokens: {
    icon: Zap,
    title: 'High Token Usage',
    bgColor: 'bg-bg-subtle',
    borderColor: 'border-border',
    iconColor: 'text-text-secondary',
  },
  shape_unknown: {
    icon: HelpCircle,
    title: 'Unknown Shape Detected',
    bgColor: 'bg-interactive-primary/10',
    borderColor: 'border-interactive-primary/30',
    iconColor: 'text-interactive-primary',
  },
  error: {
    icon: AlertCircle,
    title: 'Error',
    bgColor: 'bg-qc-error/10',
    borderColor: 'border-qc-error/30',
    iconColor: 'text-qc-error',
  },
  session_expired: {
    icon: AlertCircle,
    title: 'Session Expired',
    bgColor: 'bg-qc-error/10',
    borderColor: 'border-qc-error/30',
    iconColor: 'text-qc-error',
  },
};

// Fallback for any alert type without an explicit entry — notably the arbitrary
// suspension codes the backend attaches when a job is paused/suspended. The
// alert's own message is always shown, so this stays generic but informative.
const DEFAULT_ALERT_CONFIG: AlertStyle = {
  icon: AlertCircle,
  title: 'Notice',
  bgColor: 'bg-bg-subtle',
  borderColor: 'border-border',
  iconColor: 'text-text-secondary',
};

export const AlertsBanner: React.FC<AlertsBannerProps> = ({ alerts = [] }) => {
  const [dismissedAlerts, setDismissedAlerts] = React.useState<Set<string>>(new Set());

  if (alerts.length === 0) return null;

  const visibleAlerts = alerts.filter(alert => !dismissedAlerts.has(alert.type));

  if (visibleAlerts.length === 0) return null;

  const handleDismiss = (alertType: string) => {
    setDismissedAlerts(prev => new Set([...prev, alertType]));
  };

  return (
    <div className="space-y-2">
      {visibleAlerts.map((alert) => {
        const config = ALERT_CONFIG[alert.type] ?? DEFAULT_ALERT_CONFIG;
        const Icon = config.icon;

        return (
          <div
            key={alert.type}
            className={`${config.bgColor} border ${config.borderColor} rounded-lg p-4`}
          >
            <div className="flex items-start gap-3">
              <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${config.iconColor}`} />
              <div className="flex-1 min-w-0">
                <h4 className={`text-sm font-semibold ${theme.text.primary} mb-1`}>
                  {config.title}
                </h4>
                <p className={`text-sm ${theme.text.secondary}`}>
                  {alert.message}
                </p>
                {alert.details && (
                  <p className={`text-xs ${theme.text.tertiary} mt-2`}>
                    {alert.details}
                  </p>
                )}
              </div>
              <button
                onClick={() => handleDismiss(alert.type)}
                className="text-text-tertiary hover:text-text-secondary"
                aria-label="Dismiss alert"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
